/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   TriggerJobDialog.tsx
 *     Feature         :   114-job-infrastructure
 *     Description     :   Dialog for triggering manual job execution
 *     Constitution    :   v1.11.0 | Principle XXXVIII (Specification Testing Standards)
 *     Author          :   Peter Dobler
 *     Created         :   2026-01-28
 * =========================================================================================
 */

import React, { useState } from 'react';
import { Play, AlertTriangle } from 'lucide-react';

import { AppModal } from '../../components/ui/AppModal';
import { Badge, Button, Alert } from '../../components/ui';
import { JobDefinition, triggerJobRun, getJobTypeLabel } from '../../services/jobService';

export interface TriggerJobDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (message?: string) => void;
  jobDefinition: JobDefinition | null;
}

export default function TriggerJobDialog({
  open,
  onClose,
  onSuccess,
  jobDefinition,
}: TriggerJobDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rateLimitWarning, setRateLimitWarning] = useState<string | null>(null);

  const handleTrigger = async () => {
    if (!jobDefinition) return;

    setLoading(true);
    setError(null);

    try {
      const result = await triggerJobRun(jobDefinition.id);

      // Check for rate limit warning in response headers
      const remaining = result.rateLimitRemaining;
      if (remaining !== undefined && remaining <= 2) {
        setRateLimitWarning(`${remaining} job triggers remaining this hour`);
      }

      onSuccess(result.message);
      onClose();
    } catch (err: any) {
      // Handle specific error cases
      if (err.status === 429) {
        setError('Rate limit exceeded. Please wait before triggering another job.');
        const retryAfter = err.retryAfter;
        if (retryAfter) {
          setRateLimitWarning(`Retry after ${Math.ceil(retryAfter / 60)} minutes`);
        }
      } else {
        // The server says why it refused: no worker, collection switched off, a run already waiting.
        const refusal = err.response?.data?.error;
        setError((typeof refusal === 'string' && refusal) || err.message || 'Failed to run the job');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setRateLimitWarning(null);
      onClose();
    }
  };

  if (!jobDefinition) return null;

  return (
    <AppModal
      open={open}
      onClose={handleClose}
      size="md"
      title={
        <div className="flex items-center gap-2">
          <Play className="h-4 w-4 text-[var(--color-accent)]" />
          Trigger Job
        </div>
      }
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleTrigger}
            disabled={loading}
            loading={loading}
            leftIcon={loading ? undefined : <Play className="h-4 w-4" />}
          >
            {loading ? 'Triggering...' : 'Run Now'}
          </Button>
        </>
      }
    >
      <p className="mb-4 text-sm text-[var(--color-text-primary)]">
        This job runs for your organization only.
      </p>

      <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-bg-subtle)] p-4">
        <h6 className="mb-2 text-lg font-semibold text-[var(--color-text-primary)]">
          {jobDefinition.name}
        </h6>

        <p className="mb-4 text-sm text-[var(--color-text-secondary)]">
          {jobDefinition.description || 'No description'}
        </p>

        <div className="flex flex-wrap gap-2">
          <Badge variant="primary" isOutlined>
            {getJobTypeLabel(jobDefinition.job_type)}
          </Badge>
          <Badge variant="default" isOutlined>
            {`${jobDefinition.timeout_minutes}m timeout`}
          </Badge>
        </div>
      </div>

      {error && (
        <Alert variant="error" className="mt-4">
          {error}
        </Alert>
      )}

      {rateLimitWarning && !error && (
        <Alert variant="warning" className="mt-4" icon={<AlertTriangle className="h-5 w-5" />}>
          {rateLimitWarning}
        </Alert>
      )}
    </AppModal>
  );
}
